/**
 * 시공사례 목록 그리드 + 드래그 정렬.
 *
 * 정렬은 **조직 전체 목록의 순서**다. 그래서 검색·필터가 걸린 동안에는 드래그를 막는다 —
 * 보이는 것만 끌어 옮기면 안 보이는 사례가 어디로 갔는지 아무도 모른다. 쪽 안에서 옮긴 순서는
 * 전체 순서의 그 쪽 자리(offset)에 끼워 저장한다.
 *
 * 놓는 즉시 저장한다(별도 「저장」 버튼 없음). 실패하면 순서를 되돌리고 줄을 띄운다.
 * 읽을 때 본 전체 순서를 기준값으로 같이 보낸다 — 그사이 다른 곳에서 바뀌었으면 거절되고 서버 순서로 다시 그린다.
 */

import {
  Announcements,
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { useRouter } from "@/lib/nav";
import { useEffect, useMemo, useState, useTransition } from "react";

import { objectJosa } from "@buildpy/core/lib/josa";
import Banner from "@buildpy/ui/banner";
import EmptyState from "@buildpy/ui/empty-state";
import { cn } from "@/lib/utils";
import { spliceOrder } from "@buildpy/core/modules/list/search";
import { settleRedirect } from "@/lib/redirect-error";
import { reorderPortfolios } from "@/api/actions/portfolio";
import { isAwaitingRefresh, REORDER_SETTLE_TIMEOUT_MS } from "@buildpy/core/modules/portfolio/order";
import PortfolioCard, {
  PortfolioCardRow,
} from "@/modules/portfolio/ui/portfolio-card";

interface Props {
  // 이 쪽에 보일 사례(검색·공개 칩·쪽을 건 결과, 전체 순서대로).
  rows: PortfolioCardRow[];
  // 조직 전체 순서 — 없으면 이 쪽이 전부다.
  allIds?: string[];
  // 이 쪽 첫 장이 전체 순서의 몇 번째인지.
  offset?: number;
  // 검색·공개 칩이 걸렸으면 false — 드래그를 막는다.
  canReorder?: boolean;
  // 조직에 사례가 하나라도 있는지 — 없을 때만 「첫 장을 만들어 보세요」.
  hasAny?: boolean;
}

const PortfolioGrid = ({ rows, allIds, offset = 0, canReorder = true, hasAny }: Props) => {
  const router = useRouter();
  const rowsKey = rows.map((row) => row.id).join(",");
  // 끌어 놓은 순서 — 서버 목록(rowsKey)이 바뀌면 버리고 서버 순서를 따른다.
  const [local, setLocal] = useState<{ key: string; ids: string[] } | null>(null);
  const [failure, setFailure] = useState<{ message: string; stale: boolean } | null>(null);
  const [saving, startSaving] = useTransition();
  // 잠금 기준은 조직 전체 순서 — 이 쪽 키만 보면 다른 쪽만 바뀐 stale 새로고침에 5초를 헛잠근다.
  const orderKey = allIds ? allIds.join(",") : rowsKey;
  // 새로고침을 기다리는 서버 순서 키 — 이 키가 그대로인 동안은 드래그를 막는다.
  const [settle, setSettle] = useState<{ key: string } | null>(null);
  const settling = isAwaitingRefresh(settle, orderKey);

  // 안전 타임아웃은 저장이 끝난 뒤부터 잰다 — 저장이 길어도 새로고침 착지 전에 풀리지 않게.
  useEffect(() => {
    if (!settle || saving) return;
    const timer = setTimeout(() => setSettle((cur) => (cur === settle ? null : cur)), REORDER_SETTLE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [settle, saving]);

  // coordinateGetter 가 없으면 화살표 한 번에 25px 만 움직여 옆 카드로 넘어가지 않는다.
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const byId = useMemo(
    () => new Map(rows.map((row) => [row.id, row])),
    [rows]
  );

  /** 서버가 새 목록을 주면(추가·삭제) 우리 순서에 없는 것은 뒤에 붙인다. */
  const ordered = useMemo(() => {
    const order = local && local.key === rowsKey ? local.ids : rows.map((row) => row.id);
    const known = new Set(order);
    const tail = rows.filter((row) => !known.has(row.id));
    return [
      ...order.flatMap((id) => {
        const row = byId.get(id);
        return row ? [row] : [];
      }),
      ...tail,
    ];
  }, [byId, local, rows, rowsKey]);

  const canDrag = canReorder && !saving && !settling && ordered.length > 1;
  // 저장·새로고침 대기 중에도 핸들은 남겨 둔다(잠금만) — 없애면 키보드로 놓은 뒤 포커스가 날아간다.
  const showHandle = canReorder && ordered.length > 1;

  const titleOf = (id: UniqueIdentifier) => byId.get(String(id))?.title?.trim() || "사례";
  const placeOf = (id: UniqueIdentifier) => ordered.findIndex((row) => row.id === String(id)) + 1;
  const announcements: Announcements = {
    onDragStart: ({ active }) => `${titleOf(active.id)}${objectJosa(titleOf(active.id))} 집었습니다.`,
    onDragOver: ({ active, over }) =>
      over ? `${titleOf(active.id)} → ${placeOf(over.id)}번째 자리` : `${titleOf(active.id)} — 놓을 자리 밖`,
    onDragEnd: ({ active, over }) =>
      over ? `${titleOf(active.id)}${objectJosa(titleOf(active.id))} ${placeOf(over.id)}번째 자리에 놓았습니다.` : `${titleOf(active.id)} 옮기기를 취소했습니다.`,
    onDragCancel: ({ active }) => `${titleOf(active.id)} 옮기기를 취소했습니다.`,
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const ids = ordered.map((row) => row.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;

    const next = arrayMove(ids, from, to);
    // 기준값 = 지금 화면이 믿는 전체 순서(새로고침 전 직전 저장분 포함).
    const expected = allIds ? spliceOrder(allIds, ids, offset) : ids;
    setLocal({ key: rowsKey, ids: next });
    setFailure(null);
    setSettle({ key: orderKey });

    startSaving(async () => {
      try {
        const result = await reorderPortfolios(allIds ? spliceOrder(allIds, next, offset) : next, expected);
        if (!result.ok) {
          setLocal(null);
          setFailure({ message: result.message, stale: result.stale === true });
          if (result.stale) router.refresh();
          else setSettle(null);
          return;
        }
        router.refresh();
      } catch (error) {
        if (settleRedirect(error)) return;
        setLocal(null);
        setSettle(null);
        setFailure({ message: "순서를 저장하지 못했습니다. 다시 시도해 주세요.", stale: false });
      }
    });
  };

  if (rows.length === 0 && !(hasAny ?? false)) {
    return (
      <EmptyState>
        아직 등록한 시공사례가 없습니다. 「+ 사례」 로 첫 장을 만들어 보세요.
      </EmptyState>
    );
  }

  return (
    <div className="flex flex-col gap-y-3">
      {rows.length > 0 && (
        <span className={cn("text-xs text-muted-foreground", canDrag && "pointer-only")}>
          {/* 새로고침은 저장 트랜지션에 얽혀 saving=false 와 한 번에 그려진다 — settling 은 방어용이라 문구를 따로 두지 않는다. */}
          {canDrag
            ? "카드를 끌어 순서를 바꾸면 바로 저장됩니다. 키보드는 핸들에서 Space·Enter → 화살표 → Space·Enter."
            : saving || settling
              ? "순서 저장 중…"
              : "순서는 검색어 없이 「전체」 에서만 바꿀 수 있습니다."}
        </span>
      )}

      {failure &&
        (failure.stale ? (
          <Banner tone="warning">{failure.message}</Banner>
        ) : (
          <p className="text-sm text-destructive">{failure.message}</p>
        ))}

      {/* 고정 id — 없으면 aria-describedby 번호가 서버·클라이언트에서 달라 하이드레이션 경고가 난다. */}
      <DndContext
        accessibility={{
          announcements,
          screenReaderInstructions: {
            draggable: "Space·Enter 로 집고 화살표로 옮긴 뒤 Space·Enter 로 놓습니다. Esc 는 취소입니다.",
          },
        }}
        id="portfolio-grid-dnd"
        collisionDetection={closestCenter}
        sensors={sensors}
        onDragEnd={onDragEnd}>
        <SortableContext
          items={ordered.map((row) => row.id)}
          strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {ordered.map((row) => (
              <SortableCard key={row.id} draggable={canDrag} row={row} showHandle={showHandle} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {ordered.length === 0 && (
        <EmptyState>
          검색어·공개 여부에 해당하는 사례가 없습니다.
        </EmptyState>
      )}
    </div>
  );
};

export default PortfolioGrid;

/* ── 조각 ──────────────────────────────────────────────────── */

const SortableCard = ({
  row,
  draggable,
  showHandle,
}: {
  row: PortfolioCardRow;
  draggable: boolean;
  showHandle: boolean;
}) => {
  // disabled 면 listeners 가 비고 aria-disabled 가 붙는다 — 포인터·키보드가 같이 막힌다.
  const {
    attributes,
    isDragging,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ attributes: { roleDescription: "순서 바꾸기" }, disabled: !draggable, id: row.id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}>
      <PortfolioCard
        className={cn(isDragging && "opacity-50")}
        handle={
          showHandle ? (
            <button
              {...attributes}
              {...listeners}
              ref={setActivatorNodeRef}
              aria-label={`${row.title} 순서 바꾸기`}
              className={cn(
                "absolute left-1 top-1 rounded-md bg-overlay/55 p-1 text-overlay-foreground opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100",
                draggable ? "cursor-grab" : "cursor-wait"
              )}
              type="button">
              <GripVertical className="size-4" />
            </button>
          ) : null
        }
        row={row}
      />
    </div>
  );
};
